import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "@/lib/api";
import FileUploader from "@/components/FileUploader";

const KINDS = { case: "Case / Projeto", testimonial: "Depoimento", client: "Cliente" };
const blank = { kind: "case", title: "", client: "", segment: "", city: "", quote: "", image: "", order: 99, published: true };

export default function ShowcaseAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/admin/showcase").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(blank); setEditing(null); setOpen(true); };
  const openEdit = (it) => { setForm(it); setEditing(it.id); setOpen(true); };

  const save = async () => {
    if (!form.title && !form.quote) { toast.error("Preencha ao menos título ou depoimento."); return; }
    const payload = { ...form, order: Number(form.order) };
    try {
      if (editing) await api.put(`/admin/showcase/${editing}`, payload);
      else await api.post("/admin/showcase", payload);
      toast.success("Salvo"); setOpen(false); load();
    } catch { toast.error("Erro ao salvar"); }
  };
  const del = async (id) => { if (!window.confirm("Excluir item?")) return; await api.delete(`/admin/showcase/${id}`); load(); toast.success("Excluído"); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-white">Prova Social</h1><p className="mt-1 text-sm text-[#A1A1AA]">Cases, projetos instalados, clientes e depoimentos exibidos no site. (Não invente clientes/depoimentos.)</p></div>
        <Button data-testid="showcase-new-button" onClick={openNew} className="rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]"><Plus className="mr-1 h-4 w-4" /> Novo</Button>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`showcase-card-${it.id}`} className="flex flex-col border border-[#27272A] bg-[#151618]">
            {it.image && <img src={it.image} alt="" className="aspect-[16/10] w-full object-cover" />}
            <div className="flex flex-1 flex-col p-4">
              <span className="label-eyebrow text-[#FF5500]">{KINDS[it.kind]}</span>
              <h3 className="font-heading mt-1 text-base font-bold text-white">{it.title || it.client}</h3>
              {it.quote && <p className="mt-1 line-clamp-2 text-sm text-[#A1A1AA]">{it.quote}</p>}
              <div className="mt-3 flex gap-2">
                <button onClick={() => openEdit(it)} data-testid={`showcase-edit-${it.id}`}><Pencil className="h-4 w-4 text-[#A1A1AA] hover:text-white" /></button>
                <button onClick={() => del(it.id)} data-testid={`showcase-delete-${it.id}`}><Trash2 className="h-4 w-4 text-[#71717A] hover:text-red-400" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#A1A1AA]">Nenhum item ainda. Adicione seus primeiros cases e depoimentos reais.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-none border-[#27272A] bg-[#151618] text-white" data-testid="showcase-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Novo item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-eyebrow">Tipo</Label>
              <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger data-testid="showcase-form-kind" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#151618] text-white border-[#27272A]">{Object.entries(KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Título</Label><Input data-testid="showcase-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Cliente</Label><Input data-testid="showcase-form-client" value={form.client} onChange={(e) => set("client", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Segmento</Label><Input data-testid="showcase-form-segment" value={form.segment} onChange={(e) => set("segment", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Cidade</Label><Input data-testid="showcase-form-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            </div>
            <div><Label className="label-eyebrow">Depoimento / Descrição</Label><Textarea data-testid="showcase-form-quote" value={form.quote} onChange={(e) => set("quote", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            <div><Label className="label-eyebrow mb-2 block">Imagem</Label>
              {form.image && <img src={form.image} alt="" className="mb-2 aspect-[16/10] w-full object-cover" />}
              <FileUploader single files={form.image ? [{ url: form.image, name: "imagem" }] : []} setFiles={(arr) => { const list = typeof arr === "function" ? arr([]) : arr; set("image", list[0]?.url || ""); }} label="Enviar imagem do projeto" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Ordem</Label><Input data-testid="showcase-form-order" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <label className="mt-6 flex items-center gap-2 text-sm text-[#A1A1AA]"><input type="checkbox" data-testid="showcase-form-published" checked={form.published} onChange={(e) => set("published", e.target.checked)} /> Publicado</label>
            </div>
            <Button data-testid="showcase-form-save" onClick={save} className="w-full rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
